import { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { logger } from "@/lib/logger";
import { useAuth } from "@/contexts/AuthContext";
import { onSharedChange, readShared, writeShared } from "@/lib/sharedLocation";

// Where the customer is shopping from. Shops deliver within a radius of their
// own location, so the storefront filters by the customer's point (lat/lng)
// when we have one, and by the centre of their pincode otherwise.
const LocationContext = createContext(null);
const STORAGE_KEY = "pm_pincode";
const COORDS_KEY = "pm_location";          // {lat, lng, label}
const DEFAULT_FALLBACK = "400053";
const SKIP_KEY = "pb_loc_skip";

// The place picked on any PawBazaar site (care, community) wins over this
// site's own copy, so the location is the same everywhere.
function sharedCoords() {
  const v = readShared();
  return v && typeof v.lat === "number" && typeof v.lng === "number"
    ? { lat: v.lat, lng: v.lng, label: v.label || v.pincode || null, addressId: null, detail: v.detail || null, source: v.source || null } : null;
}

function localCoords() {
  try {
    const v = JSON.parse(localStorage.getItem(COORDS_KEY) || "null");
    return v && typeof v.lat === "number" && typeof v.lng === "number" ? v : null;
  } catch { return null; }
}

/** The shared place carries no saved-address id (it isn't this site's to
 *  share). When it is the same spot as this site's own copy, keep that id:
 *  checkout and Subscribe deliver to the saved address without asking again. */
function withOwnAddress(shared, mine) {
  const same = shared && mine?.addressId && mine.lat === shared.lat && mine.lng === shared.lng;
  return same ? { ...shared, addressId: mine.addressId } : shared;
}

function readCoords() {
  if (readShared()) return withOwnAddress(sharedCoords(), localCoords());
  return localCoords();
}

function writeCoords(v) {
  try {
    if (v) localStorage.setItem(COORDS_KEY, JSON.stringify(v));
    else localStorage.removeItem(COORDS_KEY);
  } catch { /* storage unavailable */ }
}

export function LocationProvider({ children }) {
  const { user, loading } = useAuth();
  const [pincode, setPincodeState] = useState(() => readShared()?.pincode || localStorage.getItem(STORAGE_KEY) || DEFAULT_FALLBACK);
  const [coords, setCoords] = useState(readCoords);
  const [defaultPincode, setDefaultPincode] = useState(DEFAULT_FALLBACK);
  const [userSet, setUserSet] = useState(() => !!readShared() || !!localStorage.getItem(STORAGE_KEY) || !!readCoords());
  // Tracks the user id whose default address was already applied this session,
  // so a manual Change afterwards isn't overridden by a later re-render.
  const appliedForUser = useRef(null);
  // False while a signed-in customer's saved address is still loading, so the
  // first-visit location question isn't asked of someone who already has one.
  const [savedChecked, setSavedChecked] = useState(false);
  // "Just looking": the visitor closed the first "Where should we deliver?"
  // box. Until they pick a place the shop shows every shop's products and the
  // box comes back only when they add something to the cart.
  const [browsing, setBrowsing] = useState(() => { try { return sessionStorage.getItem(SKIP_KEY) === "1"; } catch { return false; } });
  // A saved address that differs from the place picked before signing in:
  // the header asks which one to use instead of switching on its own.
  const [suggestion, setSuggestion] = useState(null);

  // Fetch platform default pincode once
  useEffect(() => {
    api.get("/config/defaults").then(({ data }) => {
      if (data.default_pincode) {
        setDefaultPincode(data.default_pincode);
        if (!localStorage.getItem(STORAGE_KEY)) setPincodeState(data.default_pincode);
      }
    }).catch((e) => { logger.warn("Failed to load /config/defaults:", e); });
  }, []);

  // `addressId` is set when the location is one of the customer's saved
  // addresses — checkout then delivers there without asking again.
  // `detail` is a longer description shown in the location popup.
  const setLocation = useCallback(({ pincode: p, lat, lng, label, addressId = null, detail = null, source = null } = {}, share = true) => {
    setSuggestion(null);
    const clean = p ? String(p).trim() : null;
    if (clean) {
      setPincodeState(clean);
      localStorage.setItem(STORAGE_KEY, clean);
    }
    const c = (typeof lat === "number" && typeof lng === "number")
      ? { lat, lng, label: label || clean || null, addressId, detail, source }
      : null;
    setCoords(c);
    writeCoords(c);
    setUserSet(true);
    if (share) writeShared({ pincode: clean, lat: c?.lat, lng: c?.lng, label: c?.label || clean, detail, source });
  }, []);

  // Picked on another PawBazaar site (or tab): use it here too, keeping our
  // saved-address id when it is the same spot (see withOwnAddress).
  useEffect(() => onSharedChange((v) => { if (v) setLocation(withOwnAddress(v, localCoords()), false); }), [setLocation]);

  // On customer login, use their default (or first) saved address — its exact
  // pin when it has one — unless a place was already picked: then offer it
  // (`suggestion`) when it's somewhere else. A manual Change afterwards is
  // respected for the rest of the session (appliedForUser guard).
  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "customer") { appliedForUser.current = null; setSavedChecked(true); return; }
    if (appliedForUser.current === user.id) return;
    appliedForUser.current = user.id;
    setSavedChecked(false);
    api.get("/customer/addresses").then(({ data }) => {
      const def = data.find(a => a.is_default) || data[0];
      if (!def?.pincode) return;
      const picked = readCoords();
      const picking = !!readShared() || !!localStorage.getItem(STORAGE_KEY) || !!picked;
      if (!picking) { setLocation(addressLocation(def)); return; }
      const near = picked && def.lat != null ? km(picked, def) < 1.5 : String(def.pincode) === String(readShared()?.pincode || localStorage.getItem(STORAGE_KEY) || "");
      if (picked?.source === "address" || near) setLocation(addressLocation(def));
      else setSuggestion(def);
    }).catch((e) => { logger.warn("Failed to auto-load customer default address:", e); })
      .finally(() => setSavedChecked(true));
  }, [user, loading, setLocation]);

  // Pincode only (typed by the customer): forget any previous exact point.
  const setPincode = useCallback((p) => {
    if (!p) return;
    setLocation({ pincode: p });
  }, [setLocation]);

  const clearPincode = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    writeShared(null);
    writeCoords(null);
    setCoords(null);
    setUserSet(false);
    setPincodeState(defaultPincode);
  }, [defaultPincode]);

  // Signing out (here, or "sign out everywhere" from another tab or site)
  // goes back to the default pincode: the customer's address isn't a guest's.
  const signedIn = useRef(false);
  useEffect(() => {
    if (loading) return;
    if (user) { signedIn.current = true; return; }
    if (signedIn.current || readCoords()?.source === "address") clearPincode();
    signedIn.current = false;
  }, [user, loading, clearPincode]);

  const skipChoice = useCallback(() => {
    try { sessionStorage.setItem(SKIP_KEY, "1"); } catch { /* storage unavailable */ }
    setBrowsing(true);
  }, []);
  const acceptSuggestion = useCallback(() => { if (suggestion) setLocation(addressLocation(suggestion)); }, [suggestion, setLocation]);
  const keepPlace = useCallback(() => setSuggestion(null), []);
  const looking = !userSet && browsing;

  // Query params for listing / serviceability endpoints. Just looking: no
  // place, so every shop's products show.
  const locationParams = useMemo(
    () => (looking ? {} : coords ? { lat: coords.lat, lng: coords.lng, pincode } : (pincode ? { pincode } : {})),
    [looking, coords, pincode]
  );
  const locationLabel = looking ? "All of Mumbai" : (coords?.label || pincode);
  const addressId = coords?.addressId || null;

  const value = useMemo(
    () => ({ pincode, coords, locationParams, locationLabel, addressId, defaultPincode, userSet, isDefault: !userSet, looking,
             mustChoose: !userSet && savedChecked && !browsing, setPincode, setLocation, clearPincode, skipChoice,
             suggestion, acceptSuggestion, keepPlace }),
    [pincode, coords, locationParams, locationLabel, addressId, defaultPincode, userSet, savedChecked, browsing, looking, setPincode, setLocation,
     clearPincode, skipChoice, suggestion, acceptSuggestion, keepPlace]
  );

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}

// Straight-line km between two {lat, lng}.
function km(a, b) {
  const r = (d) => (d * Math.PI) / 180;
  const h = Math.sin(r(b.lat - a.lat) / 2) ** 2 + Math.cos(r(a.lat)) * Math.cos(r(b.lat)) * Math.sin(r(b.lng - a.lng) / 2) ** 2;
  return 12742 * Math.asin(Math.sqrt(h));
}

/** Location object for a saved address (see setLocation). */
export function addressLocation(a) {
  return {
    pincode: a.pincode, lat: a.lat, lng: a.lng, label: a.label || a.area,
    addressId: a.needs_location ? null : a.id, source: "address",
    detail: [a.flat_no, a.address].filter(Boolean).join(", "),
  };
}

/** Ask the header to open the "Change delivery location" popup. */
export function openLocationPicker() {
  window.dispatchEvent(new Event("pm:open-location"));
}

export function useLocation() {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error("useLocation must be used within LocationProvider");
  return ctx;
}
